// KPI Builder — small derived lookups over the answers. No query building here.
import { GAP_A, GAP_B, KPI_FILTERS, RECIPES, SOURCES } from 'constants/kpiBuilderCatalogue';
import { customGroup, customMeasure, customSource, isPersonalField } from './custom';
import { LEARNER_GROUPS } from './learnerGroups';
import { BuilderAnswers, Column, DataSource, GroupBy, KpiFilterDef, LearnerGroup, Measure, RecipeId, SourceId } from './types';

export const source = (a: BuilderAnswers): DataSource | null => (a.src === 'custom' ? customSource(a.custom) : a.src ? SOURCES[a.src] : null);
export const sourceB = (a: BuilderAnswers): DataSource | null => (a.srcB ? SOURCES[a.srcB] : null);
export const measureOf = (a: BuilderAnswers): Measure | null =>
  (a.measure === 'custom' ? customMeasure(a.custom) : source(a)?.measures.find((m) => m.id === a.measure) ?? null);
export const groupOf = (a: BuilderAnswers): GroupBy | null =>
  (a.group === 'custom' ? customGroup(a.custom) : source(a)?.groups.find((g) => g.id === a.group) ?? null);
export const selectedGroups = (a: BuilderAnswers): LearnerGroup[] => LEARNER_GROUPS.filter((g) => a.segs.includes(g.id));
export const isPreService = (a: BuilderAnswers): boolean => selectedGroups(a).some((g) => g.preService);
/** Columns ticked from the catalogue, then any the admin typed. Personal data is never included. */
export const listColumns = (a: BuilderAnswers): Column[] => {
  const typedCols = a.custom.columns.filter((c) => c.field.trim() && c.label.trim() && !isPersonalField(c.field)).map((c, i) => ({ id: `typed${i}`, label: c.label.trim(), field: c.field.trim(), kind: c.kind ?? 'value' }));
  return [...(source(a)?.cols ?? []).filter((c) => a.cols.includes(c.id) && !c.pii), ...typedCols];
};
export const availableKpiFilters = (a: BuilderAnswers): KpiFilterDef[] =>
  (a.src && !['progress', 'ready'].includes(a.recipe ?? '') ? KPI_FILTERS.filter((f) => f.applies.includes(a.src as SourceId)) : []);
export const chosenKpiFilters = (a: BuilderAnswers): KpiFilterDef[] => availableKpiFilters(a).filter((f) => a.kpiFilters.includes(f.id));

/** Running total is meaningless for averages. */
export const cumulativeAllowed = (a: BuilderAnswers): boolean => measureOf(a)?.agg !== 'avg';
export const effectiveCumulative = (a: BuilderAnswers): boolean => a.cumulative && cumulativeAllowed(a);

/** Allowed second lists for the gap recipe, excluding the first list. */
export const gapBOptions = (a: BuilderAnswers): SourceId[] => GAP_B.filter((s) => s !== a.src);
export const gapAOptions = (): SourceId[] => GAP_A;
export const recipeSources = (a: BuilderAnswers): SourceId[] => RECIPES.find((r) => r.id === a.recipe)?.sources ?? [];


// Questions that fix their own data sources: a gap needs two different ones, a progress comparison two, a ready-made chart its own.
export const ANCHORED: RecipeId[] = ['gap', 'progress', 'ready'];
export const sourcesReady = (a: BuilderAnswers): boolean => {
  if (a.recipe === 'ready') return !!a.src;
  return !!a.src && !!a.srcB && (a.recipe === 'progress' || a.src !== a.srcB);
};

